import React from "react";
import { defaultTint } from "~/utils/constants";
import type { ColorSource, FederatedEventHandler, Graphics as PixiGraphics } from "pixi.js";
import { Rectangle } from "pixi.js";
import {
  edstFontName,
  eramFontDimensionMap,
  EramFontName,
  getBitmapTextStyles,
  useFocused,
} from "@poscon/shared-frontend";

type TableFieldProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  minWidth?: number;
  maxWidth?: number;
  fontName?: EramFontName;
  text?: string;
  tint?: ColorSource;
  fillColor?: ColorSource;
  onmousedown?: FederatedEventHandler;
  onmouseup?: FederatedEventHandler;
  disabled?: boolean;
  borderTint?: ColorSource;
  hoverTint?: ColorSource;
  selected?: boolean;
  focusOnHover?: boolean;
  textX?: number;
  children?: React.ReactNode;
};

export const TableField = ({
  x = 0,
  y = 0,
  text: _text = "",
  textX,
  fontName = edstFontName,
  minWidth = 0,
  width: _width = eramFontDimensionMap[fontName].width * _text.length + 3,
  maxWidth = _width,
  height = eramFontDimensionMap[fontName].height + 2,
  tint = defaultTint,
  fillColor = 0x000000,
  borderTint,
  hoverTint = 0xf0f0f0,
  selected,
  disabled,
  focusOnHover = true,
  onmousedown,
  onmouseup,
  children,
}: TableFieldProps) => {
  const ref = React.useRef<PixiGraphics>(null);
  const focused = useFocused(ref);
  let text = fontName?.startsWith("eramText") ? _text.toUpperCase() : _text;
  const width = Math.min(Math.max(_width, minWidth), maxWidth);
  const textLength = text.length * eramFontDimensionMap[fontName].width;

  if (textLength > width) {
    text = text.slice(0, Math.floor(width / eramFontDimensionMap[fontName].width));
  }

  return (
    <container x={x} y={y} sortableChildren eventMode={disabled ? "none" : "passive"}>
      <graphics
        zIndex={0}
        ref={ref}
        eventMode="static"
        draw={(graphics) => {
          graphics.clear();
          graphics
            .rect(0, 0, width, height - 1)
            .fill({ color: selected ? tint : fillColor, alpha: selected ? 1 : 0 });
          if (focused && focusOnHover) {
            graphics.stroke({ width: 1, color: hoverTint, alignment: 0 });
          } else if (borderTint) {
            graphics.stroke({ width: 1, color: borderTint, alignment: 0 });
          }
        }}
        hitArea={new Rectangle(0, 0, width, height)}
        onMouseDown={onmousedown}
        onRightDown={onmousedown}
        onMouseUp={onmouseup}
      />
      {children}
      {!children && text && (
        <bitmapText
          eventMode="none"
          text={text}
          x={textX ?? Math.floor((width - textLength) / 2 - 1)}
          y={2}
          style={{ ...getBitmapTextStyles(fontName), fill: selected ? fillColor : tint }}
        />
      )}
    </container>
  );
};
